import { useMemo, useState } from 'react';
import { Archive, ArchiveRestore, ChevronDown, Loader2, MoreVertical, Pencil, Plus } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  useToast,
} from '@/components/ui';
import { useSpaceContext } from '@/contexts/SpaceContext';
import { useCreateLabel, useLabels, useUpdateLabel } from '@/hooks/useLabels';
import { meetsRole } from '@/lib/space-roles';
import { cn } from '@/lib/cn';
import { LABEL_COLOR_SWATCHES } from '@/components/labels/colors';
import type { Label } from '@/types/label';

/**
 * Per-space label management. Renders active labels as the primary list
 * with rename/recolor/archive affordances, and a collapsed archived
 * section below for restore. Backend gates create/edit/archive at
 * member+; viewers see a read-only list.
 */
export function LabelsTab() {
  const { spaceId, role, space, isPersonal } = useSpaceContext();
  const { success, error: showError } = useToast();
  const canManage = meetsRole(role, 'member');

  // `includeArchived: true` so the tab can render both sections from one
  // cache. Same `/me/labels` query the rail uses — no extra round-trip.
  const { data: labels, isLoading } = useLabels(spaceId, { includeArchived: true });
  const update = useUpdateLabel();
  const create = useCreateLabel();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Label | null>(null);
  const [archivedOpen, setArchivedOpen] = useState(false);

  const { active, archived } = useMemo(() => {
    const list = labels ?? [];
    return {
      active: list.filter((l) => !l.archived),
      archived: list.filter((l) => l.archived),
    };
  }, [labels]);

  const handleRename = async (label: Label, name: string) => {
    const next = name.trim();
    if (!next || next === label.name) return;
    try {
      await update.mutateAsync({
        spaceId: label.spaceId,
        labelId: label.labelId,
        name: next,
      });
      success('Label renamed');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to rename');
    }
  };

  const handleDescription = async (label: Label, description: string) => {
    if (description.trim() === (label.description ?? '').trim()) return;
    try {
      await update.mutateAsync({
        spaceId: label.spaceId,
        labelId: label.labelId,
        description: description.trim(),
      });
      success('Description updated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update description');
    }
  };

  const handleRecolor = async (label: Label, color: string) => {
    try {
      await update.mutateAsync({
        spaceId: label.spaceId,
        labelId: label.labelId,
        color,
      });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update color');
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      await update.mutateAsync({
        spaceId: archiveTarget.spaceId,
        labelId: archiveTarget.labelId,
        archived: true,
      });
      success(`Archived “${archiveTarget.name}”`);
      setArchiveTarget(null);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to archive');
    }
  };

  const handleUnarchive = async (label: Label) => {
    try {
      await update.mutateAsync({
        spaceId: label.spaceId,
        labelId: label.labelId,
        archived: false,
      });
      success(`Restored “${label.name}”`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to restore');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Labels"
          description={
            isPersonal
              ? 'Personal label set. Use labels to organise items in your inbox.'
              : `Shared label set for ${space?.name ?? 'this space'}. Members can attach labels to inbox items; archived labels disappear from pickers but stay attached to historical items.`
          }
          action={
            canManage ? (
              <NewLabelButton
                spaceId={spaceId}
                existingNames={(labels ?? []).map((l) => l.name)}
                onCreated={() => success('Label created')}
                onError={(msg) => showError(msg)}
                isPending={create.isPending}
                onSubmit={async (input) => {
                  await create.mutateAsync({ spaceId, ...input });
                }}
                onUnarchiveExisting={async (label) => {
                  await update.mutateAsync({
                    spaceId: label.spaceId,
                    labelId: label.labelId,
                    archived: false,
                  });
                }}
                archived={archived}
              />
            ) : undefined
          }
        />

        {isLoading ? (
          <div className="space-y-2" aria-hidden>
            {Array.from({ length: 3 }).map((_, i) => (
              <LabelRowSkeleton key={i} />
            ))}
          </div>
        ) : active.length === 0 ? (
          <p className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
            No labels yet.{' '}
            {canManage
              ? 'Click “New label” to add one.'
              : 'An admin or member can create the first one.'}
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {active.map((label) => (
              <LabelRow
                key={label.labelId}
                label={label}
                editing={editingId === label.labelId}
                canManage={canManage}
                onStartEdit={() => setEditingId(label.labelId)}
                onCancelEdit={() => setEditingId(null)}
                onRename={(name) => handleRename(label, name)}
                onSaveDescription={(d) => handleDescription(label, d)}
                onRecolor={(c) => handleRecolor(label, c)}
                onArchive={() => setArchiveTarget(label)}
              />
            ))}
          </ul>
        )}
      </Card>

      {archived.length > 0 && (
        <Card>
          <button
            type="button"
            onClick={() => setArchivedOpen((v) => !v)}
            aria-expanded={archivedOpen}
            className="-m-2 flex w-[calc(100%+1rem)] items-center justify-between rounded-md p-2 text-left hover:bg-muted/50"
          >
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                Archived{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  ({archived.length})
                </span>
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Archived labels are hidden from pickers and the inbox rail but stay
                attached to historical items.
              </p>
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                archivedOpen && 'rotate-180',
              )}
            />
          </button>
          {archivedOpen && (
            <ul className="mt-4 divide-y divide-border rounded-md border border-border">
              {archived.map((label) => (
                <ArchivedLabelRow
                  key={label.labelId}
                  label={label}
                  canManage={canManage}
                  onUnarchive={() => handleUnarchive(label)}
                />
              ))}
            </ul>
          )}
        </Card>
      )}

      <ConfirmModal
        isOpen={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
        title="Archive this label?"
        message={
          `“${archiveTarget?.name ?? 'This label'}” will disappear from the inbox rail and label picker. ` +
          `Items already labelled keep their chips and you can restore the label at any time.`
        }
        confirmText="Archive"
        variant="default"
        isLoading={update.isPending}
      />
    </div>
  );
}

function LabelRow({
  label,
  editing,
  canManage,
  onStartEdit,
  onCancelEdit,
  onRename,
  onSaveDescription,
  onRecolor,
  onArchive,
}: {
  label: Label;
  editing: boolean;
  canManage: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onRename: (name: string) => void;
  onSaveDescription: (description: string) => void;
  onRecolor: (color: string) => void;
  onArchive: () => void;
}) {
  const [name, setName] = useState(label.name);
  const [description, setDescription] = useState(label.description ?? '');

  // Snap form state back to canonical values whenever the user opens or
  // closes the editor — keeps cancel cheap and avoids stale edits leaking
  // across rows.
  function startEdit() {
    setName(label.name);
    setDescription(label.description ?? '');
    onStartEdit();
  }

  function commit() {
    onRename(name);
    onSaveDescription(description);
    onCancelEdit();
  }

  if (editing) {
    return (
      <li className="px-4 py-3">
        <div className="flex items-start gap-3">
          <SwatchPicker color={label.color} onPick={onRecolor} />
          <div className="flex-1 space-y-2">
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commit();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelEdit();
                }
              }}
              placeholder="Label name"
              className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
              className="w-full resize-none rounded-md border border-input bg-background px-2 py-1.5 text-xs"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancelEdit}
                className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <Button size="sm" onClick={commit}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span
        className="h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: label.color || 'currentColor' }}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{label.name}</p>
        {label.description ? (
          <p className="truncate text-xs text-muted-foreground">{label.description}</p>
        ) : null}
      </div>
      <span className="hidden font-mono text-[10px] text-muted-foreground/70 sm:inline">
        {label.slug}
      </span>
      {canManage && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Label actions"
              className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={startEdit}>
              <Pencil className="mr-2 h-3.5 w-3.5" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={onArchive}
              className="text-red-600 focus:text-red-700"
            >
              <Archive className="mr-2 h-3.5 w-3.5" />
              Archive
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  );
}

function ArchivedLabelRow({
  label,
  canManage,
  onUnarchive,
}: {
  label: Label;
  canManage: boolean;
  onUnarchive: () => void;
}) {
  const archivedAt = label.archivedAt
    ? new Date(label.archivedAt).toLocaleDateString()
    : null;
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span
        className="h-2.5 w-2.5 shrink-0 rounded-full opacity-50"
        style={{ backgroundColor: label.color || 'currentColor' }}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-muted-foreground">{label.name}</p>
        {archivedAt && (
          <p className="text-xs text-muted-foreground/70">Archived {archivedAt}</p>
        )}
      </div>
      {canManage && (
        <Button variant="outline" size="sm" onClick={onUnarchive}>
          <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />
          Restore
        </Button>
      )}
    </li>
  );
}

function SwatchPicker({
  color,
  onPick,
}: {
  color?: string;
  onPick: (color: string) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Pick color"
          className="mt-1 h-5 w-5 shrink-0 rounded-full border-2 border-transparent ring-1 ring-border transition-transform hover:scale-110"
          style={{ backgroundColor: color || 'var(--muted-foreground)' }}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <div className="grid grid-cols-4 gap-1.5">
          {LABEL_COLOR_SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Color ${c}`}
              onClick={() => onPick(c)}
              className={cn(
                'h-5 w-5 rounded-full border-2 transition-transform',
                color === c
                  ? 'scale-110 border-foreground'
                  : 'border-transparent hover:scale-105',
              )}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function LabelRowSkeleton() {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <Skeleton className="h-2.5 w-2.5 shrink-0 rounded-full" />
      <Skeleton className="h-4 flex-1 max-w-[40%]" />
    </div>
  );
}

interface CreateLabelInput {
  name: string;
  color: string;
  description?: string;
}

function NewLabelButton({
  spaceId,
  existingNames,
  archived,
  onSubmit,
  onUnarchiveExisting,
  onCreated,
  onError,
  isPending,
}: {
  spaceId: string;
  existingNames: string[];
  archived: Label[];
  onSubmit: (input: CreateLabelInput) => Promise<void>;
  onUnarchiveExisting: (label: Label) => Promise<void>;
  onCreated: () => void;
  onError: (msg: string) => void;
  isPending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(LABEL_COLOR_SWATCHES[0]);
  const [description, setDescription] = useState('');

  // 409s come back as backend messages — we pre-detect a likely collision
  // against the archived list so we can offer a one-click Unarchive instead
  // of forcing the user to retry with a different name.
  const archivedCollision = useMemo(() => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return null;
    return (
      archived.find((l) => l.name.toLowerCase() === trimmed) ?? null
    );
  }, [name, archived]);

  const activeCollision = useMemo(() => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return false;
    return existingNames
      .filter((n) => archived.every((a) => a.name !== n))
      .some((n) => n.toLowerCase() === trimmed);
  }, [name, existingNames, archived]);

  function reset() {
    setName('');
    setColor(LABEL_COLOR_SWATCHES[0]);
    setDescription('');
  }

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await onSubmit({
        name: trimmed,
        color,
        description: description.trim() || undefined,
      });
      onCreated();
      reset();
      setOpen(false);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not create label');
    }
  }

  async function restore() {
    if (!archivedCollision) return;
    try {
      await onUnarchiveExisting(archivedCollision);
      onCreated();
      reset();
      setOpen(false);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not restore label');
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <PopoverTrigger asChild>
        <Button size="sm">
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          New label
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-3"
        onCloseAutoFocus={(e) => e.preventDefault()}
        // Capturing the space at the prop level lets the helpful "for this
        // space" copy below stay accurate when the popover renders in a
        // portal far away from the current tab.
        data-space-id={spaceId}
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-muted-foreground">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !archivedCollision && !activeCollision) {
                  e.preventDefault();
                  void submit();
                }
              }}
              placeholder="Billing"
              autoFocus
              className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
            />
            {archivedCollision && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400">
                An archived label named “{archivedCollision.name}” already exists.
              </p>
            )}
            {!archivedCollision && activeCollision && (
              <p className="text-[11px] text-red-600 dark:text-red-400">
                A label with this name already exists in this space.
              </p>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-muted-foreground">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
              rows={2}
              className="w-full resize-none rounded-md border border-input bg-background px-2 py-1.5 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-muted-foreground">Color</label>
            <div className="flex flex-wrap gap-1.5">
              {LABEL_COLOR_SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Color ${c}`}
                  onClick={() => setColor(c)}
                  className={cn(
                    'h-5 w-5 rounded-full border-2 transition-transform',
                    color === c
                      ? 'scale-110 border-foreground'
                      : 'border-transparent hover:scale-105',
                  )}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              disabled={isPending}
            >
              Cancel
            </button>
            {archivedCollision ? (
              <Button size="sm" onClick={restore} isLoading={isPending}>
                <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />
                Restore
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={submit}
                disabled={!name.trim() || activeCollision || isPending}
              >
                {isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                Create
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
